import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TextField } from 'new/elements/TextField';
import { extractApiError } from '../../utils/errorUtils';

export interface CloneToolsetDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string }) => Promise<void>;
  isLoading?: boolean;
  originalName?: string;
  originalDescription?: string;
}

export const CloneToolsetDialog: React.FC<CloneToolsetDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  originalName,
  originalDescription,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Pre-fill when dialog opens
  useEffect(() => {
    if (open && originalName) {
      setName(`Copy of ${originalName}`);
      setDescription(originalDescription || '');
      setError(null);
      setTouched(false);
    }
  }, [open, originalName, originalDescription]);

  const handleClose = () => {
    if (isLoading) return;
    setName('');
    setDescription('');
    setError(null);
    setTouched(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
      });
    } catch (err) {
      setError(extractApiError(err, 'Failed to clone toolset'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Clone Toolset</Typography>
        <IconButton size="small" onClick={handleClose} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Create a copy of this toolset with all its tools. The new toolset will be active
            immediately.
          </Typography>
          <TextField
            label="Toolset Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for the cloned toolset"
            required
            error={touched && !name.trim()}
            helperText={touched && !name.trim() ? 'Name is required' : ''}
            onBlur={() => setTouched(true)}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this toolset (optional)"
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !name.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Cloning...' : 'Clone Toolset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
